/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomSetter, useStateXSetter } from '@cloudio-saas/statex';
import {
  Button,
  Input,
  Grid,
  FormControl,
  InputLabel,
  IconButton,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AnyFormulaColumn, Platform } from 'componentregistry';
import { useFormulaColumnsEditor } from 'components/DataGrid/useColumns';
import { DialogPopup } from 'components/Popup/DialogPopup';
import { runtimePath } from 'designer/atoms';
import { expressionPropsAtom } from 'expression/builder/state';
import Icon from 'icons/Icon';
import { useRuntimeProperty } from 'platform/PlatformHooks';
import React, { useRef } from 'react';
import { ulid } from 'ulid';

const useStyles = makeStyles((theme: Theme) => ({
  footer: {
    padding: '8px 16px',
    height: 48,
    backgroundColor: theme.palette.background.default,
  },
  footerbtn: {
    height: 32,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  entries: {
    display: 'flex',
    gap: 2,
  },
}));

export const defaultNewFormulaColumn = {
  type: 'FormulaStringColumn',
  props: {
    label: { type: 'text', value: '' },
    width: 200,
    formula: { type: 'expression', value: '' },
  },
  parent: '',
  itemId: '',
  children: [],
} as AnyFormulaColumn;

type FormulaColumnPopupProps = {
  pageUid: string;
  itemId: string;
  appUid: string;
  platform: Platform;
};

const types = [
  {
    value: 'FormulaStringColumn',
    label: 'String',
  },
  { value: 'FormulaNumberColumn', label: 'Number' },
];
const FormulaColumnPopup = ({
  itemId,
  pageUid,
  appUid,
  platform,
}: FormulaColumnPopupProps): React.ReactElement => {
  const ref = useRef<HTMLDivElement>(null);
  const set = useStateXSetter();
  const setExpressionProps = useStateXAtomSetter(expressionPropsAtom, {
    params: { appUid, pageId: pageUid, itemId },
  });

  const path = [
    ...runtimePath({
      appUid,
      pageId: pageUid,
      itemId,
      key: 'formulaColumn',
      subkey: 'props',
    }),
    'formula',
  ];

  const [showAddColumnPopup, setShowAddColumnPopup] = useRuntimeProperty({
    appUid,
    itemId,
    pageId: pageUid,
    prop: 'showAddColumnPopup',
    defaultValue: false,
  });

  const [formulaColumn, setFormulaColumn] = useRuntimeProperty({
    appUid,
    itemId,
    pageId: pageUid,
    prop: 'formulaColumn',
    defaultValue: defaultNewFormulaColumn,
  });

  const returnType =
    formulaColumn.type === 'FormulaStringColumn' ? 'string' : 'number';

  const { createNew, updateRecord } = useFormulaColumnsEditor(
    appUid,
    pageUid,
    itemId,
  );

  const openExpressionBuilder = () => {
    setExpressionProps({
      open: true,
      path,
      returnType,
      context: {
        appUid,
        pageId: pageUid,
        itemId,
        property: 'formulaColumn',
        data: {},
      },
      value: formulaColumn.props.formula,
      type: 'expressionObject',
    });
  };

  const onAddNewFormulaColumn = async () => {
    await createNew(
      {
        ...formulaColumn,
        itemId: `f_${ulid()}`,
        parent: itemId,
      },
      formulaColumn.parent,
      set,
    );
    onClose();
  };

  const onUpdateFormulaColumn = () => {
    updateRecord(formulaColumn.itemId, formulaColumn);
    onClose();
  };

  const onSave = () => {
    if (formulaColumn.itemId) {
      onUpdateFormulaColumn();
    } else {
      onAddNewFormulaColumn();
    }
  };
  const classes = useStyles();
  const HEIGHT = 500;
  const WIDTH = 800;

  const onClose = () => {
    setShowAddColumnPopup(false);
  };

  const header = formulaColumn.itemId
    ? 'Update New Formula Column'
    : 'Create Formula Column';
  const body = (
    <Grid container item xs spacing={2}>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <InputLabel>Column title</InputLabel>
          <Input
            value={formulaColumn.props.label ?? ''}
            onChange={(e) =>
              setFormulaColumn({
                ...formulaColumn,
                props: {
                  ...formulaColumn.props,
                  label: { type: 'text', value: e.target.value },
                },
              })
            }
            placeholder="Give a title to this column"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl
          variant="standard"
          required
          fullWidth
          component="fieldset"
          size="small"
        >
          <FormLabel component="legend">Type</FormLabel>
          <RadioGroup
            aria-label="Type"
            name="Type"
            value={formulaColumn.type}
            onChange={(e, value) =>
              setFormulaColumn({
                ...formulaColumn,
                type: value,
              } as AnyFormulaColumn)
            }
            row
          >
            {types.map(({ value, label }) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={<span /* className={classes.label} */>{label}</span>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <InputLabel>Column Width</InputLabel>
          <Input
            value={formulaColumn.props.width ?? ''}
            onChange={(e) =>
              setFormulaColumn({
                ...formulaColumn,
                props: {
                  ...formulaColumn.props,
                  width: parseInt(e.target.value, 10),
                },
              })
            }
            placeholder="Give a width to this column"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl variant="standard" fullWidth>
          <InputLabel>Formula</InputLabel>
          <Input
            startAdornment={
              <IconButton onClick={openExpressionBuilder}>
                <Icon iconName="sigma" />
              </IconButton>
            }
            value={formulaColumn.props.formula.value ?? ''}
            onChange={(e) =>
              setFormulaColumn({
                ...formulaColumn,
                props: {
                  ...formulaColumn.props,
                  formula: { type: 'expression', value: e.target.value },
                },
              })
            }
            placeholder="Enter the formula"
          />
        </FormControl>
      </Grid>
    </Grid>
  );

  const popupActions = (
    <div>
      <Grid container item xs className={classes.footer}>
        <Grid item xs style={{ flexGrow: 1 }} />

        <Grid item xs style={{ paddingLeft: 16, flexGrow: 0, flexBasis: 160 }}>
          <Button onClick={onSave} className={classes.footerbtn} fullWidth>
            {formulaColumn.itemId ? 'Update' : 'Add'}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
  return (
    <DialogPopup
      metadata
      platform={platform}
      appUid={appUid}
      popupref={ref}
      popupHeight={HEIGHT}
      open={showAddColumnPopup}
      popupWidth={WIDTH}
      modal={false}
      onClose={onClose}
      bodyChildren={body}
      Header={header}
      popupActions={popupActions}
      pageId="$sidebar$"
      itemId={itemId}
    />
  );
};

export default FormulaColumnPopup;
